import { useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Button, Group, MultiSelect, Select, SimpleGrid, Stack, Text, TextInput, rem,
} from '@mantine/core';
import {
  IconArrowLeft, IconBuildingStore, IconInfoCircle, IconLink, IconMailForward, IconShieldCheck,
  IconUserCheck, IconUserPlus,
} from '@tabler/icons-react';
import { useForm, schemaResolver } from '@mantine/form';
import { z } from 'zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useDebouncedValue, useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { isValidEmail } from '@/shared/lib/validators';
import { useAuth } from '@/app/providers/AuthProvider';
import { useAziendeAssociate } from '@/features/tenants/aziendeSelezionabili';
import { canManageSubTenants } from '@/features/tenants/tenantAccess';
import { checkEmail, createOrAssociateUser, linkUserInGroup, type CreateUserOutcome } from '@/features/users/api';
import { fetchRoles } from '@/features/roles/api';
import { MaeAdvice } from '@/shared/components/MaeAdvice';
import { MaeCard, MaeField, MaePageHead, MaeShell } from '@/shared/desktop/MaeShell';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';
import { mw } from '@/shared/mobile/tokens';
import { userFacingMessage } from '@/shared/api/errorMessage';

export const Route = createFileRoute('/_app/users/new')({ component: NewUserPage });

/**
 * Un utente nasce **per invito**, e non c'è una seconda strada.
 *
 * Qui c'era una seconda modalità, «Crea con password», che mandava `passwordHash` a
 * `POST /api/users`. Quel campo **non esiste più**: è stato rimosso dalla risorsa in v1.5 per
 * sicurezza — un attributo JSON:API si può leggere e filtrare, e un hash di password su cui si può
 * fare `filter=` è un problema anche se scriverlo sembrava comodo. La modalità quindi rispondeva
 * `422` su un attributo inesistente, **sempre**, e nessuno l'aveva notato perché la modalità
 * predefinita era e resta l'invito.
 *
 * L'abbiamo rimossa invece di ripararla: una via che non può funzionare va tolta, non lasciata a
 * dare un errore che nomina un campo di cui chi compila non ha mai sentito parlare. E l'invito è
 * meglio comunque: nessuna password transita, e la prima che l'utente ha è una che ha scelto lui.
 *
 * ── La pagina ──
 *
 * Tre domande in tre riquadri — *chi inviti*, *come si chiama*, *cosa può fare* — invece di un
 * unico blocco di campi: l'email è la sola cosa obbligatoria e merita di stare da sola, perché è
 * quella che decide **cosa succederà davvero** al salvataggio. Mentre si digita, il BE la
 * classifica (`account/check-email`) e il riquadro dice l'esito prima del clic: già nel tenant,
 * nel gruppo societario, o esterno — con dentro il rimedio, che per il gruppo è il bottone
 * «Collega» e non un invito che sarebbe la strada più lunga.
 */
function NewUserPage() {
  const navigate = useNavigate();
  return (
    <MaeShell>
      <MaePageHead
        title="Nuovo utente"
        sub="Nessuna password viene digitata qui: l’utente riceve un invito e sceglie la sua."
        icon={<IconUserPlus size={23} />}
        actions={(
          <Button variant="light" leftSection={<IconArrowLeft size={16} />}
            onClick={() => void navigate({ to: '/users' })}>
            Torna a Utenti
          </Button>
        )}
      />
      <InviteForm />
    </MaeShell>
  );
}

// ─── Invito/associazione via workflow POST /api/user-profiles ────────────────
// Nessuna password: il BE assegna quella di default e invia un'email di benvenuto/OTP.
// Se l'utente esiste già viene associato/invitato a questo tenant.

const inviteSchema = z.object({
  email: z.string().email('Email non valida'),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  roleIds: z.array(z.string()),
});

/** Colore notifica: ok pieno = verde, ok parziale (email KO) = arancio, errore = rosso. */
function outcomeColor(o: CreateUserOutcome): string {
  if (!o.ok) return 'red';
  return o.kind === 'emailFailed' ? 'orange' : 'green';
}

function InviteForm() {
  const navigate = useNavigate();
  const { decoded } = useAuth();
  const isMobile = useIsMobileShell();
  /* Due colonne solo da 75em in su: sotto, la seconda colonna diventerebbe una striscia stretta
     accanto a campi già stretti — due colonne strette sono peggio di una giusta. */
  const largo = useMediaQuery('(min-width: 75em)') ?? false;

  /*
   * DOVE FINISCE L'UTENTE.
   *
   * Prima non era scritto da nessuna parte: l'invito partiva sull'azienda di sessione (o su
   * senza che il modulo la nominasse). Chi amministra più società lo scopriva
   * dopo. Il valore viaggia come `tenantDestinationId` — il contratto lo prevede già.
   *
   * Le opzioni sono le **proprie** aziende: sono quelle in cui il server accetta di creare. Per
   * un'azienda del perimetro che non è tua la strada è il cambio azienda in testata.
   */
  /*
   * ⚠️ QUESTO MENU NON E' MAI COMPARSO, E IL MOTIVO STA IN UNA RIGA.
   *
   * Qui c'era una mappatura con un cast addosso — `t as { id?: string; … }` — e
   * `AssociatedTenant` **non ha `id`**: ha `tenantId`. Quindi `value` era sempre `''`, il
   * `.filter((o) => o.value)` finale svuotava l'elenco a ogni giro, e
   * `puoScegliere = aziende.length > 1` era **sempre falso**.
   *
   * Cioè: la tendina qui sotto non si è mai vista, `tenantDestinationId` non è mai stato spedito,
   * e chi amministra più società ha continuato a invitare sull'azienda di sessione — esattamente
   * il difetto che il commento qui sopra dice di aver chiuso. Nessun errore da nessuna parte: a
   * schermo sembrava «hai una sola azienda», che è una frase plausibile.
   *
   * La mappatura ora sta in `aziendeSelezionabili.ts`, tipizzata, in un posto solo — era la terza
   * volta che `t.id` al posto di `t.tenantId` passava inosservato sotto un cast.
   */
  const own = useAziendeAssociate();
  const companies = useMemo(
    () => own.map((a) => ({ value: a.id, label: a.nome })),
    [own],
  );
  const [tenantId, setTenantId] = useState<string>(decoded?.tenantId ?? '');
  const destinationName =
    companies.find((a) => a.value.toLowerCase() === (tenantId ?? '').toLowerCase())?.label ?? 'Azienda corrente';
  /* Con una sola azienda un menu a tendina è rumore: si scrive dove finisce e basta. */
  const canChoose = companies.length > 1;
  const form = useForm({
    mode: 'controlled',
    initialValues: { email: '', firstName: '', lastName: '', roleIds: [] as string[] },
    validate: schemaResolver(inviteSchema, { sync: true }),
  });

  // Anti-enumeration (Sprint 1 §1): classifica l'email lato server prima del submit
  // per anticipare l'esito (già presente / gruppo / esterno). Rate-limit 20/min per-IP.
  const [debouncedEmail] = useDebouncedValue(form.values.email.trim().toLowerCase(), 500);
  const emailOk = isValidEmail(debouncedEmail);
  const scopeQ = useQuery({
    queryKey: ['check-email', debouncedEmail],
    enabled: emailOk,
    staleTime: 60_000,
    retry: false,
    queryFn: () => checkEmail(debouncedEmail),
  });
  const scope = emailOk && !scopeQ.isFetching ? scopeQ.data : undefined;

  const linkInGroup = useMutation({
    mutationFn: () => linkUserInGroup(debouncedEmail, tenantId || (decoded?.tenantId ?? '')),
    onSuccess: (r) => {
      notifications.show({
        color: 'green',
        message: r.alreadyLinked ? 'Utente già collegato a questa azienda.' : 'Utente collegato all’azienda: può accedere da subito.',
      });
      void navigate({ to: '/users' });
    },
    onError: (err) => {
      const msg = userFacingMessage(err, 'Errore');
      notifications.show({ color: 'red', title: 'Collegamento non riuscito', message: msg });
    },
  });

  // I ruoli sono quelli dell'azienda in cui si è entrati: il server li filtra sul token.
  const rolesQ = useQuery({ queryKey: ['roles'], queryFn: fetchRoles });
  const roleOptions = (rolesQ.data?.data ?? []).map((r) => ({
    value: r.id,
    label: r.isFromMaster ? `${r.name ?? r.id} (sistema)` : (r.name ?? r.id),
  }));

  const create = useMutation({
    mutationFn: (v: typeof form.values) =>
      createOrAssociateUser({
        email: v.email, firstName: v.firstName, lastName: v.lastName, roleIds: v.roleIds,
        /* Solo se diversa dalla sessione: passarla sempre significherebbe mandare un header
           anche quando non c'è niente da spostare, e con l'override acceso lo mette già l'API. */
        ...(canChoose && tenantId && tenantId !== decoded?.tenantId ? { tenantDestinationId: tenantId } : {}),
      }),
    onSuccess: (outcome) => {
      notifications.show({
        color: outcomeColor(outcome),
        title: outcome.ok ? 'Operazione completata' : 'Operazione non riuscita',
        message: outcome.message,
      });
      if (outcome.ok) void navigate({ to: '/users' });
    },
    onError: (err) => {
      notifications.show({ color: 'red', title: 'Operazione fallita', message: userFacingMessage(err, 'Errore di rete') });
    },
  });

  const onSubmit = form.onSubmit((v) => void create.mutate(v));
  /* Già dentro il tenant: l'invito non aggiungerebbe nulla e la strada giusta è la sua scheda. */
  const alreadyPresent = scope?.scope === 'sameTenant';

  return (
    <form onSubmit={onSubmit}>
      {/*
        DUE COLONNE, NON UN MODULO PIÙ LARGO.

        Il modulo resta della sua misura — un campo email lungo mezzo schermo si compila peggio,
        non meglio — ma la pagina non lascia più metà larghezza vuota: accanto ci va quello che
        prima stava in mezzo ai campi, cioè dove finisce l'utente e cosa succederà all'email che
        stai scrivendo. Sotto i 75em torna una colonna sola: due colonne strette sono peggio di
        una giusta.
      */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: largo ? 'minmax(0, 760px) minmax(300px, 380px)' : 'minmax(0, 1fr)',
          gap: 20,
          alignItems: 'start',
        }}
      >
      <Stack gap={14} style={{ minWidth: 0 }}>
        <Section
          icon={<IconMailForward size={19} />}
          titolo="Chi inviti"
          sub="L’email decide cosa succede al salvataggio: si controlla mentre la scrivi."
        >
          <MaeField label="Email" required>
            <TextInput
              type="email"
              placeholder="nome@azienda.it"
              size={isMobile ? 'md' : 'sm'}
              {...form.getInputProps('email')}
            />
          </MaeField>

        </Section>

        <Section
          icon={<IconUserCheck size={19} />}
          titolo="Come si chiama"
          sub="Facoltativo: se lo lasci vuoto, lo compila l’utente al primo accesso."
        >
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={14}>
            <MaeField label="Nome">
              <TextInput size={isMobile ? 'md' : 'sm'} {...form.getInputProps('firstName')} />
            </MaeField>
            <MaeField label="Cognome">
              <TextInput size={isMobile ? 'md' : 'sm'} {...form.getInputProps('lastName')} />
            </MaeField>
          </SimpleGrid>
        </Section>

        <Section
          icon={<IconShieldCheck size={19} />}
          titolo="Cosa può fare"
          sub="I ruoli si cambiano anche dopo, dalla scheda dell’utente."
        >
          <MaeField label="Ruoli" hint="Lascia vuoto per assegnare il ruolo base «User».">
            <MultiSelect
              placeholder={roleOptions.length ? 'Seleziona uno o più ruoli' : rolesQ.isError ? 'Elenco ruoli non leggibile' : 'Nessun ruolo disponibile'}
              data={roleOptions}
              searchable
              clearable
              size={isMobile ? 'md' : 'sm'}
              disabled={rolesQ.isLoading || rolesQ.isError}
              {...form.getInputProps('roleIds')}
            />
          </MaeField>
          {rolesQ.isError && (
            <MaeAdvice
              tone="warning"
              title="Elenco ruoli non caricato"
              action={{ label: 'Riprova', onClick: () => void rolesQ.refetch() }}
            >
              Senza l’elenco non posso mostrarti i ruoli assegnabili. Puoi invitare comunque
              l’utente — prenderà il ruolo base «User» — e aggiungere i ruoli dalla sua scheda.
            </MaeAdvice>
          )}
        </Section>

        {/* Le azioni vanno a capo su mobile e occupano tutta la riga: un bottone primario
            largo mezzo pollice in fondo a un modulo lungo è il posto in cui si sbaglia. */}
        <Group justify="flex-end" gap={10} wrap="wrap" className="mae-pagehead-actions">
          <Button variant="subtle" size={isMobile ? 'md' : 'sm'} fullWidth={isMobile}
            onClick={() => void navigate({ to: '/users' })}>
            Annulla
          </Button>
          <Button type="submit" size={isMobile ? 'md' : 'sm'} fullWidth={isMobile}
            leftSection={<IconMailForward size={16} />}
            loading={create.isPending} disabled={alreadyPresent}>
            Invia invito
          </Button>
        </Group>
      </Stack>

      {/* La colonna di destra: prima la destinazione, poi le conseguenze. In quest'ordine
          perché la destinazione è una scelta e le conseguenze sono una lettura. */}
      <Stack gap={14} style={{ minWidth: 0, position: largo ? 'sticky' : undefined, top: largo ? 78 : undefined }}>
        <MaeCard padding={isMobile ? 15 : 18}>
          <Group gap={10} wrap="nowrap" align="center" mb={12}>
            <span style={{
              width: 32, height: 32, borderRadius: 10, flexShrink: 0, background: mw.accentTint,
              color: mw.accentText, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <IconBuildingStore size={17} />
            </span>
            <Text style={{ fontSize: 'var(--fs-body)', fontWeight: 700, color: mw.ink900 }}>Dove finisce</Text>
          </Group>

          {canChoose ? (
            <MaeField
              label="Azienda di destinazione"
              hint="L’utente verrà creato o associato qui, e i ruoli sono quelli di questa azienda."
            >
              <Select
                /* `MaeField` disegna l'etichetta come testo, non come `<label>`: senza questo il
                   campo, per chi usa uno screen reader, è una tendina senza nome. */
                aria-label="Azienda di destinazione"
                data={companies}
                value={tenantId}
                onChange={(v) => setTenantId(v ?? decoded?.tenantId ?? '')}
                allowDeselect={false}
                searchable
                size={isMobile ? 'md' : 'sm'}
              />
            </MaeField>
          ) : (
            <Stack gap={6}>
              <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink900, fontWeight: 600 }}>{destinationName}</Text>
              <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, lineHeight: 1.5 }}>
                L’utente verrà creato in quest’azienda. Per un’altra società del gruppo, cambia
                azienda dalla testata.
              </Text>
            </Stack>
          )}
        </MaeCard>

        {/* Gli avvisi sull'email: erano fra i campi e spostavano il modulo comparendo. */}
        <Stack gap={12}>
          {scopeQ.isError && (
            <MaeAdvice
              tone="warning"
              title="Non ho potuto controllare questa email"
              action={{ label: 'Riprova il controllo', onClick: () => void scopeQ.refetch() }}
            >
              Il controllo preventivo non ha risposto, quindi non posso dirti in anticipo se
              l’utente esiste già. L’invito funziona lo stesso: il sistema rifà la verifica al
              salvataggio.
            </MaeAdvice>
          )}
          {scope?.rateLimited && (
            <MaeAdvice
              tone="warning"
              title="Troppe verifiche in poco tempo"
              selfRemedy="Attendi un minuto per rivedere l’esito in anticipo, oppure procedi con l’invito: il controllo vero lo fa comunque il sistema."
            >
              Il controllo preventivo dell’email è limitato a venti richieste al minuto.
            </MaeAdvice>
          )}
          {alreadyPresent && (
            <MaeAdvice
              tone="warning"
              icon={IconUserCheck}
              title="Questo utente è già in questa azienda"
              action={{ label: 'Vai all’elenco utenti', onClick: () => void navigate({ to: '/users' }) }}
            >
              L’invito non è necessario e non verrà inviato: l’account esiste già qui. Se serve
              cambiargli ruoli o riaprirgli l’accesso, si fa dalla sua scheda.
            </MaeAdvice>
          )}
          {scope?.scope === 'myGroup' && (
            <MaeAdvice
              tone="info"
              icon={IconLink}
              title="Esiste già nel tuo gruppo societario"
              {...(canManageSubTenants(decoded)
                ? {
                  action: {
                    label: linkInGroup.isPending ? 'Collego…' : 'Collega a questa azienda',
                    onClick: () => linkInGroup.mutate(),
                  },
                }
                : { selfRemedy: 'Per collegarlo servono i permessi sui sottotenant: chiedi a chi amministra il gruppo.' })}
            >
              Puoi collegarlo direttamente, senza invito: riceverà una notifica trasparente e
              potrà accedere da subito, senza rifare la registrazione.
            </MaeAdvice>
          )}
          {scope?.scope === 'external' && (
            <MaeAdvice tone="info" icon={IconInfoCircle} title="Utente esterno al gruppo">
              Partirà una <b>richiesta di consenso</b> via email: l’invito resta in attesa finché
              non la accetta, e fino ad allora non vedrà nulla di questa azienda.
            </MaeAdvice>
          )}
        </Stack>
      </Stack>
      </div>
    </form>
  );
}

/** Un passo del modulo: icon-box brand, titolo, spiegazione, campi. */
function Section({ icon, titolo, sub, children }: {
  icon: React.ReactNode; titolo: string; sub: string; children: React.ReactNode;
}) {
  const isMobile = useIsMobileShell();
  return (
    <MaeCard padding={isMobile ? 15 : 18}>
      <Group gap={12} wrap="nowrap" align="flex-start" mb={14}>
        <span style={{
          width: 38, height: rem(38), borderRadius: 11, flexShrink: 0, background: mw.brandFill,
          color: 'var(--on-brand)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 6px 18px rgba(84,4,9,0.22)',
        }}>
          {icon}
        </span>
        <div style={{ minWidth: 0 }}>
          <Text style={{ fontSize: 'var(--fs-body-lg)', fontWeight: 700, color: mw.ink900, letterSpacing: '-0.01em' }}>{titolo}</Text>
          <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, marginTop: 2, lineHeight: 1.45 }}>{sub}</Text>
        </div>
      </Group>
      <Stack gap={14}>{children}</Stack>
    </MaeCard>
  );
}
